import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  cancelBook,
  deleteBook,
  downloadBookFile,
  getBookContent,
  getBookSummary,
  regenerateBook,
  retryBook,
  updateBookCategory,
} from "../api/client";
import {
  ApiError,
  type Book,
  type CreateBookOptions,
  type EventRow,
  type OutputFormat,
} from "../api/types";
import ConfirmDialog from "../components/ConfirmDialog";
import EventLog from "../components/EventLog";
import FidelityNotes from "../components/FidelityNotes";
import GuardrailNotes from "../components/GuardrailNotes";
import { AlertIcon, DownloadIcon, RefreshIcon, TrashIcon } from "../components/Icon";
import MetadataPanel from "../components/MetadataPanel";
import Reader from "../components/Reader";
import SelectionToolbar from "../components/SelectionToolbar";
import RegenerateDialog from "../components/RegenerateDialog";
import StageStepper from "../components/StageStepper";
import TocSidebar from "../components/TocSidebar";
import { useBook } from "../hooks/useBook";
import { useBookStream } from "../hooks/useBookStream";
import { useCategories } from "../hooks/useCategories";
import { queryKeys } from "../hooks/queryKeys";
import { extractChapterHeadings } from "../lib/markdown";
import {
  getBookTab,
  getFontSize,
  getScrollPos,
  setBookTab as persistBookTab,
  setFontSize as persistFontSize,
  setScrollPos,
  type BookTab,
  type FontSize,
} from "../lib/storage";

export default function BookDetail() {
  const { id } = useParams<{ id: string }>();
  const bookId = id ?? "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: book, isLoading, isError } = useBook(id);
  const { events } = useBookStream(id, book?.status);

  const [showDelete, setShowDelete] = useState(false);
  const [showRegenerate, setShowRegenerate] = useState(false);
  const [showBuildAnyway, setShowBuildAnyway] = useState(false);

  const cancelMutation = useMutation<Book, ApiError, void>({
    mutationFn: () => cancelBook(bookId),
    onSuccess: (b) => queryClient.setQueryData(queryKeys.book(bookId), b),
  });

  const retryMutation = useMutation<Book, ApiError, void>({
    mutationFn: () => retryBook(bookId),
    onSuccess: (b) => queryClient.setQueryData(queryKeys.book(bookId), b),
  });

  const regenerateMutation = useMutation<Book, ApiError, CreateBookOptions>({
    mutationFn: (options) => regenerateBook(bookId, options),
    onSuccess: (b) => {
      queryClient.setQueryData(queryKeys.book(bookId), b);
      void queryClient.invalidateQueries({ queryKey: queryKeys.bookContent(bookId) });
      setShowRegenerate(false);
      setShowBuildAnyway(false);
    },
  });

  const deleteMutation = useMutation<void, ApiError, void>({
    mutationFn: () => deleteBook(bookId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["books"] });
      navigate("/");
    },
  });

  if (!id) {
    return <NotFound />;
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <Link to="/" className="mb-6 inline-block text-sm text-muted hover:text-text">
        ← Library
      </Link>

      {isLoading ? (
        <p className="text-muted">Loading…</p>
      ) : isError || !book ? (
        <NotFound />
      ) : book.status === "completed" ? (
        <CompletedView
          book={book}
          onRegenerate={() => setShowRegenerate(true)}
          onDelete={() => setShowDelete(true)}
        />
      ) : book.status === "failed" || book.status === "canceled" ? (
        <TerminalIssueView
          book={book}
          events={events}
          onRetry={() => retryMutation.mutate()}
          retrying={retryMutation.isPending}
          onBuildAnyway={() => setShowBuildAnyway(true)}
          onDelete={() => setShowDelete(true)}
        />
      ) : (
        <ProcessingView
          book={book}
          events={events}
          onCancel={() => cancelMutation.mutate()}
          canceling={cancelMutation.isPending}
        />
      )}

      {book ? (
        <>
          <ConfirmDialog
            open={showDelete}
            title="Delete book"
            description={
              <>
                Delete “{book.book_title ?? book.video_title ?? "this book"}”? This removes it and all
                generated files. This can't be undone.
              </>
            }
            confirmLabel="Delete"
            danger
            pending={deleteMutation.isPending}
            onConfirm={() => deleteMutation.mutate()}
            onClose={() => setShowDelete(false)}
          />
          <RegenerateDialog
            open={showRegenerate}
            book={book}
            pending={regenerateMutation.isPending}
            onClose={() => setShowRegenerate(false)}
            onConfirm={(options) => regenerateMutation.mutate(options)}
          />
          <ConfirmDialog
            open={showBuildAnyway}
            title="Build anyway"
            description="Guardrails flagged this transcript as a possible prompt-injection attack. Build it anyway?"
            confirmLabel="Build anyway"
            warn
            pending={regenerateMutation.isPending}
            onConfirm={() => regenerateMutation.mutate({ ignore_guardrails: true })}
            onClose={() => setShowBuildAnyway(false)}
          />
        </>
      ) : null}
    </div>
  );
}

/**
 * Downloads cannot be plain links: the browser would navigate without the
 * Authorization header and the API would refuse. Fetching keeps the request
 * authenticated, at the cost of having to report failures ourselves.
 */
function DownloadButton({
  book,
  fmt,
  label,
}: {
  book: Book;
  fmt: OutputFormat;
  label: string;
}) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function handleClick() {
    setFailed(false);
    setBusy(true);
    try {
      await downloadBookFile(book.id, fmt);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      title={failed ? "Download failed - try again" : undefined}
      className={
        "inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm font-medium hover:bg-surface disabled:opacity-60 " +
        (failed ? "border-danger text-danger" : "border-border text-text")
      }
    >
      <DownloadIcon width={14} height={14} />
      {busy ? "Preparing…" : failed ? `${label} failed` : label}
    </button>
  );
}


function NotFound() {
  return (
    <div className="py-16 text-center">
      <p className="text-muted">This book could not be found.</p>
      <Link to="/" className="mt-3 inline-block text-accent hover:underline">
        Back to library
      </Link>
    </div>
  );
}

function ProcessingView({
  book,
  events,
  onCancel,
  canceling,
}: {
  book: Book;
  events: EventRow[];
  onCancel: () => void;
  canceling: boolean;
}) {
  const title = book.book_title ?? book.video_title ?? "Processing…";
  return (
    <div className="mx-auto max-w-2xl">
      <header className="mb-8">
        <p className="text-sm text-muted">{book.channel ?? "-"}</p>
        <h1 className="font-serif text-2xl font-semibold text-text sm:text-3xl">{title}</h1>
        {book.status === "queued" ? (
          <p className="mt-2 text-sm text-muted">Queued - waiting for a worker to pick this up.</p>
        ) : null}
      </header>

      <div className="mb-6 rounded-lg border border-border bg-surface p-6">
        <StageStepper stage={book.stage} chaptersDone={book.chapters_done} chaptersTotal={book.chapters_total} />
      </div>

      <div className="mb-6">
        <button
          type="button"
          onClick={onCancel}
          disabled={canceling}
          className="rounded-md border border-border px-4 py-2 text-sm font-medium text-text hover:bg-surface disabled:opacity-50"
        >
          {canceling ? "Canceling…" : "Cancel"}
        </button>
      </div>

      <EventLog events={events} />
    </div>
  );
}

function TerminalIssueView({
  book,
  events,
  onRetry,
  retrying,
  onBuildAnyway,
  onDelete,
}: {
  book: Book;
  events: EventRow[];
  onRetry: () => void;
  retrying: boolean;
  onBuildAnyway: () => void;
  onDelete: () => void;
}) {
  const failed = book.status === "failed";
  const blocked = book.error_code === "CONTENT_BLOCKED";
  const title = book.book_title ?? book.video_title ?? "Untitled";

  return (
    <div className="mx-auto max-w-2xl">
      <header className="mb-6">
        <p className="text-sm text-muted">{book.channel ?? "-"}</p>
        <h1 className="font-serif text-2xl font-semibold text-text sm:text-3xl">{title}</h1>
      </header>

      <div
        className={
          "mb-6 rounded-lg border p-5 " + (failed ? "border-danger-surface bg-danger-surface" : "border-border bg-surface")
        }
      >
        <p className={"flex items-center gap-2 text-sm font-medium " + (failed ? "text-danger" : "text-muted")}>
          <AlertIcon width={16} height={16} />
          {failed ? "Something went wrong" : "Canceled"}
        </p>
        {failed && book.error_message ? <p className="mt-2 text-sm text-text">{book.error_message}</p> : null}
        {failed && book.error_hint ? <p className="mt-1 text-sm text-muted">{book.error_hint}</p> : null}
        {!failed ? <p className="mt-2 text-sm text-text">This book's processing was canceled.</p> : null}
      </div>

      {book.guardrail_notes.length > 0 ? (
        <div className="mb-6">
          <GuardrailNotes notes={book.guardrail_notes} />
        </div>
      ) : null}

      <div className="mb-6 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          className="inline-flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-strong disabled:opacity-50"
        >
          <RefreshIcon width={14} height={14} />
          {retrying ? "Retrying…" : "Retry"}
        </button>
        {blocked ? (
          <button
            type="button"
            onClick={onBuildAnyway}
            className="inline-flex items-center gap-2 rounded-md border border-warn-border bg-warn-surface px-4 py-2 text-sm font-medium text-warn hover:opacity-90"
          >
            <AlertIcon width={14} height={14} />
            Build anyway
          </button>
        ) : null}
        <button
          type="button"
          onClick={onDelete}
          className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-medium text-text hover:bg-surface"
        >
          <TrashIcon width={14} height={14} />
          Delete
        </button>
      </div>

      <EventLog events={events} defaultOpen={failed} />
    </div>
  );
}

const FONT_SIZES: FontSize[] = ["S", "M", "L"];

function CompletedView({
  book,
  onRegenerate,
  onDelete,
}: {
  book: Book;
  onRegenerate: () => void;
  onDelete: () => void;
}) {
  const queryClient = useQueryClient();
  const { data: categoriesData } = useCategories();
  const categories = categoriesData?.categories ?? [];

  const updateCategoryMutation = useMutation<Book, ApiError, string | null>({
    mutationFn: (categoryId) => updateBookCategory(book.id, categoryId),
    onSuccess: (b) => {
      queryClient.setQueryData(queryKeys.book(book.id), b);
      void queryClient.invalidateQueries({ queryKey: queryKeys.categories() });
      void queryClient.invalidateQueries({ queryKey: ["books"] });
    },
  });

  const contentQuery = useQuery({
    queryKey: queryKeys.bookContent(book.id),
    queryFn: () => getBookContent(book.id),
  });

  const [tab, setTabState] = useState<BookTab>(() => getBookTab(book.id));
  const summaryQuery = useQuery({
    queryKey: queryKeys.bookSummary(book.id),
    queryFn: () => getBookSummary(book.id),
    enabled: tab === "summary" && book.has_summary,
  });

  const [fontSize, setFontSizeState] = useState<FontSize>(() => getFontSize());
  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const readerRef = useRef<HTMLDivElement>(null);
  const restoredScroll = useRef(false);

  const headings = useMemo(
    () => (contentQuery.data ? extractChapterHeadings(contentQuery.data) : []),
    [contentQuery.data],
  );

  function changeFontSize(size: FontSize) {
    setFontSizeState(size);
    persistFontSize(size);
  }

  function changeTab(next: BookTab) {
    setTabState(next);
    persistBookTab(book.id, next);
  }

  // Restore remembered scroll position once, after the content first renders.
  // Scoped to the Book tab — a Summary-tab view has no book scroll position to restore.
  useEffect(() => {
    if (tab !== "book" || !contentQuery.data || restoredScroll.current) return;
    restoredScroll.current = true;
    const saved = getScrollPos(book.id);
    if (saved != null) {
      requestAnimationFrame(() => window.scrollTo({ top: saved }));
    }
  }, [tab, contentQuery.data, book.id]);

  // Remember scroll position as the reader scrolls. Scoped to the Book tab.
  useEffect(() => {
    if (tab !== "book" || !contentQuery.data) return;
    let handle: ReturnType<typeof setTimeout> | null = null;
    function onScroll() {
      if (handle) clearTimeout(handle);
      handle = setTimeout(() => setScrollPos(book.id, window.scrollY), 250);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (handle) clearTimeout(handle);
    };
  }, [tab, contentQuery.data, book.id]);

  // Scroll-spy: highlight the chapter heading currently nearest the top of the viewport.
  useEffect(() => {
    if (tab !== "book" || !contentQuery.data || headings.length === 0) return;
    const container = readerRef.current;
    if (!container) return;

    const elements = headings
      .map((h) => container.querySelector<HTMLElement>(`#${CSS.escape(h.slug)}`))
      .filter((el): el is HTMLElement => !!el);
    if (elements.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible.length > 0) {
          setActiveSlug(visible[0].target.id);
        }
      },
      { rootMargin: "0px 0px -70% 0px", threshold: 0 },
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [tab, contentQuery.data, headings]);

  function handleNavigate(slug: string) {
    const el = document.getElementById(slug);
    if (!el) return;
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    el.scrollIntoView({ behavior: prefersReduced ? "auto" : "smooth", block: "start" });
  }

  const title = book.book_title ?? book.video_title ?? "Untitled";

  return (
    <div>
      <header className="mb-6 border-b border-border pb-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-sm text-muted">{book.channel ?? "-"}</p>
            <h1 className="font-serif text-2xl font-semibold text-text sm:text-3xl">{title}</h1>
            {book.subtitle ? <p className="mt-1 font-serif text-lg text-muted">{book.subtitle}</p> : null}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label htmlFor="book-category" className="text-sm text-muted">
              Category:
            </label>
            <select
              id="book-category"
              value={book.category_id ?? ""}
              onChange={(e) => updateCategoryMutation.mutate(e.target.value || null)}
              disabled={updateCategoryMutation.isPending}
              className="rounded-md border border-border bg-surface pl-3 pr-9 py-2 text-sm text-text disabled:opacity-50"
            >
              <option value="">No category</option>
              {categories.map((category) => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>
            {book.has_epub ? (
              <DownloadButton book={book} fmt="epub" label="EPUB" />
            ) : null}
            {book.has_pdf ? (
              <DownloadButton book={book} fmt="pdf" label="PDF" />
            ) : null}
            {book.has_md ? (
              <DownloadButton book={book} fmt="md" label="Markdown" />
            ) : null}
            <button
              type="button"
              onClick={onRegenerate}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm font-medium text-text hover:bg-surface"
            >
              <RefreshIcon width={14} height={14} /> Regenerate
            </button>
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center gap-1.5 rounded-md border border-border px-3 py-2 text-sm font-medium text-danger hover:bg-danger-surface"
            >
              <TrashIcon width={14} height={14} /> Delete
            </button>
          </div>
        </div>
      </header>

      {book.guardrail_notes.length > 0 ? (
        <div className="mb-6">
          <GuardrailNotes notes={book.guardrail_notes} />
        </div>
      ) : null}

      {book.fidelity_notes.length > 0 ? (
        <div className="mb-6">
          <FidelityNotes notes={book.fidelity_notes} />
        </div>
      ) : null}

      <div className="mb-6">
        <MetadataPanel book={book} />
      </div>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex border-b border-border" role="tablist" aria-label="Book view">
          <button
            type="button"
            role="tab"
            aria-selected={tab === "book"}
            onClick={() => changeTab("book")}
            className={
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium " +
              (tab === "book" ? "border-accent text-accent" : "border-transparent text-muted hover:text-text")
            }
          >
            Book
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === "summary"}
            onClick={() => changeTab("summary")}
            className={
              "-mb-px border-b-2 px-3 py-2 text-sm font-medium " +
              (tab === "summary" ? "border-accent text-accent" : "border-transparent text-muted hover:text-text")
            }
          >
            Summary
          </button>
        </div>

        <div className="flex items-center gap-1.5 text-sm">
          <span className="mr-1 text-muted">Text size</span>
          {FONT_SIZES.map((size) => (
            <button
              key={size}
              type="button"
              onClick={() => changeFontSize(size)}
              aria-pressed={fontSize === size}
              aria-label={`Font size ${size}`}
              className={
                "h-7 w-7 rounded-md border text-xs font-medium " +
                (fontSize === size ? "border-accent bg-accent-soft text-accent-strong" : "border-border text-muted hover:bg-surface")
              }
            >
              {size}
            </button>
          ))}
        </div>
      </div>

      {tab === "book" ? (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[200px_1fr]">
          <aside className="hidden lg:block">
            <div className="sticky top-8 max-h-[calc(100vh-4rem)] overflow-y-auto">
              <TocSidebar headings={headings} activeSlug={activeSlug} onNavigate={handleNavigate} />
            </div>
          </aside>
          <div>
            {contentQuery.isLoading ? (
              <p className="text-muted">Loading book…</p>
            ) : contentQuery.isError ? (
              <p className="text-danger">Could not load the book text.</p>
            ) : (
              <SelectionToolbar source={{ bookTitle: book.book_title, videoUrl: book.url }}>
                <Reader ref={readerRef} markdown={contentQuery.data ?? ""} fontSize={fontSize} />
              </SelectionToolbar>
            )}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[200px_1fr]">
          <aside className="hidden lg:block" aria-hidden="true" />
          <div>
            {!book.has_summary ? (
              <p className="text-sm text-muted">
                This book was built before summaries existed. Regenerate the book to create one.
              </p>
            ) : summaryQuery.isLoading ? (
              <p className="text-muted">Loading summary…</p>
            ) : summaryQuery.isError ? (
              <p className="text-danger">Could not load the summary.</p>
            ) : (
              <SelectionToolbar source={{ bookTitle: book.book_title, videoUrl: book.url }}>
                <Reader markdown={summaryQuery.data ?? ""} fontSize={fontSize} />
              </SelectionToolbar>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
